import React, {useCallback, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import {Button, Spinner} from 'reactstrap';
import {Image as ImageIcon, Paperclip, Send, Square, X} from 'lucide-react';
import {toast} from 'react-toastify';
import {useUploadAttachment} from '../api/runHooks';
import {AttachmentInfo} from '../api/types';

interface PendingFile {
    localId: string;
    fileName: string;
    status: 'uploading' | 'uploaded' | 'failed';
    info?: AttachmentInfo;
}

interface MessageComposerProps {
    disabled: boolean;
    disabledReason?: string | null;
    isRunActive: boolean;
    awaitingApproval: boolean;
    supportsImages: boolean;
    /** Extra controls rendered beside the attach button (e.g. the tool connections button). */
    leftAccessory?: React.ReactNode;
    onSend: (text: string, attachmentIds: string[]) => void;
    onStop: () => void;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
    disabled,
    disabledReason,
    isRunActive,
    awaitingApproval,
    supportsImages,
    leftAccessory,
    onSend,
    onStop
}) => {
    const [text, setText] = useState('');
    const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const uploadAttachment = useUploadAttachment();

    const uploadFile = useCallback(
        async (file: File) => {
            if (file.type.startsWith('image/') && !supportsImages) {
                toast.error('The current model does not support image input.');
                return;
            }

            const localId = `${file.name}-${Date.now()}-${Math.random()}`;
            setPendingFiles(files => [
                ...files,
                {localId, fileName: file.name, status: 'uploading'}
            ]);

            try {
                const info = await uploadAttachment.mutateAsync(file);
                setPendingFiles(files =>
                    files.map(pending =>
                        pending.localId === localId
                            ? {...pending, status: 'uploaded', info}
                            : pending
                    )
                );
            } catch (error) {
                setPendingFiles(files =>
                    files.filter(pending => pending.localId !== localId)
                );
                toast.error(
                    error instanceof Error
                        ? error.message
                        : `Failed to upload ${file.name}`
                );
            }
        },
        [supportsImages, uploadAttachment]
    );

    const {getRootProps, getInputProps, open, isDragActive} = useDropzone({
        noClick: true,
        noKeyboard: true,
        onDrop: acceptedFiles =>
            acceptedFiles.forEach(file => void uploadFile(file))
    });

    const handlePaste = (event: React.ClipboardEvent) => {
        const imageItems = Array.from(event.clipboardData.items).filter(item =>
            item.type.startsWith('image/')
        );
        for (const item of imageItems) {
            const file = item.getAsFile();
            if (file) {
                event.preventDefault();
                void uploadFile(file);
            }
        }
    };

    const removePendingFile = (localId: string) => {
        setPendingFiles(files =>
            files.filter(file => file.localId !== localId)
        );
    };

    const isUploading = pendingFiles.some(file => file.status === 'uploading');
    const canSend =
        !disabled &&
        !isRunActive &&
        !isUploading &&
        (text.trim().length > 0 ||
            pendingFiles.some(f => f.status === 'uploaded'));

    const handleSend = () => {
        if (!canSend) {
            return;
        }

        const attachmentIds = pendingFiles
            .filter(file => file.status === 'uploaded' && file.info)
            .map(file => file.info!.attachmentId);

        onSend(text.trim(), attachmentIds);
        setText('');
        setPendingFiles([]);
        textareaRef.current?.focus();
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    };

    if (disabled && disabledReason) {
        return (
            <div className="alert alert-info small mb-0">{disabledReason}</div>
        );
    }

    return (
        <div
            {...getRootProps()}
            className={`border rounded p-2 ${isDragActive ? 'border-primary bg-light' : ''}`}
        >
            <input {...getInputProps()} />

            {pendingFiles.length > 0 && (
                <div className="d-flex flex-wrap gap-1 mb-1">
                    {pendingFiles.map(file => (
                        <span
                            key={file.localId}
                            className="badge bg-light text-dark border d-flex align-items-center gap-1"
                        >
                            {file.status === 'uploading' && (
                                <Spinner size="sm" />
                            )}
                            {file.info?.isImage ? (
                                <ImageIcon size={12} />
                            ) : (
                                <Paperclip size={12} />
                            )}
                            {file.fileName}
                            <X
                                size={12}
                                role="button"
                                onClick={() => removePendingFile(file.localId)}
                            />
                        </span>
                    ))}
                </div>
            )}

            <textarea
                ref={textareaRef}
                className="form-control border-0 shadow-none p-1"
                rows={2}
                placeholder={
                    awaitingApproval
                        ? 'Waiting for your approval above…'
                        : 'Ask the assistant…'
                }
                value={text}
                disabled={disabled || awaitingApproval}
                onChange={event => setText(event.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                style={{resize: 'none'}}
            />

            <div className="d-flex justify-content-between align-items-center mt-1">
                <div className="d-flex align-items-center gap-3">
                    <Button
                        color="link"
                        size="sm"
                        className="text-muted p-0"
                        disabled={disabled || awaitingApproval}
                        onClick={open}
                        title="Attach files"
                    >
                        <Paperclip size={16} />
                    </Button>
                    {leftAccessory}
                </div>

                {isRunActive ? (
                    <Button
                        outline
                        color="danger"
                        size="sm"
                        onClick={onStop}
                        className="d-flex align-items-center gap-1"
                    >
                        <Square size={14} /> Stop
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        size="sm"
                        disabled={!canSend}
                        onClick={handleSend}
                        className="d-flex align-items-center gap-1"
                    >
                        <Send size={14} /> Send
                    </Button>
                )}
            </div>
        </div>
    );
};

export default MessageComposer;
